import * as React from 'react';
import cx from 'classnames';
import isArray from 'lodash/isArray';
import {
  ErrorTag,
  SuccessTag,
  SetDefaultButton,
  DeactivateButton,
  ActivateButton,
  InactiveText,
  WarningTag,
  MetaSettingsPopover,
  PopoverType,
  NAText,
  IMetaSettingsPopoverProps,
} from '@frontend/app/components/InstagramMeta/MetaItems';
import {
  IInstagramAccount,
  disableInstagramTableButton,
} from '@frontend/app/components/InstagramMeta/MetaItems/InstagramAccountTable';
import styles from '@frontend/app/components/InstagramMeta/MetaItems/MetaItems.module.scss';
import { AdPublishersNumericText } from './AdPublishersNumericText';
import { AdPublishersEditButton } from './AdPublishersEditButton';
import { CellStatus } from './utils/composeCells';
import { IAdAccount } from './AdAccountTable';
import { IBusiness } from './AllowlistingTable';

const requiresFacebookLoginFromRecord = (record: IInstagramAccount): boolean => {
  return (
    record.tokenDebug?.status !== 'ACTIVE'
    && record.tokenDebug?.error_code === '190'
    && record.tokenDebug?.error_subcode === '459'
  );
};

export const renderFeatures = (popoverProps: IMetaSettingsPopoverProps, TagComponent: React.ReactNode) => (
  <MetaSettingsPopover {...popoverProps}>{TagComponent}</MetaSettingsPopover>
);

const renderTagComponent = (type: CellStatus) => {
  switch (type) {
    case CellStatus.ACTIVE:
      return SuccessTag;
    case CellStatus.INACTIVE:
      return InactiveText;
    case CellStatus.WARNING:
      return WarningTag;
    case CellStatus.DISCONNECTED:
      return ErrorTag;
    case CellStatus.NA:
      return NAText;
  }
};

export const renderSocialListeningTag = (record: IInstagramAccount, igEstimatedImpressionsEnabled: boolean) => {
  const { features } = record.socialListening;
  const TagComponent = renderTagComponent(record.socialListening.cell);
  let popoverType = PopoverType.FEATURES;
  if (record.socialListening.cell === CellStatus.DISCONNECTED) {
    if (requiresFacebookLoginFromRecord(record)) {
      popoverType = PopoverType.FACEBOOK_LOGIN_REQUIRED;
    } else {
      popoverType = PopoverType.DISCONNECTED;
    }
  }
  if (record.socialListening.cell === CellStatus.NA || record.socialListening.cell === CellStatus.INACTIVE) {
    popoverType = PopoverType.HIDDEN;
  }
  const filteredFeatures = features.filter((feature) => {
    if (feature.feature === 'Story mentions') {
      return igEstimatedImpressionsEnabled;
    }
    return true;
  });
  return renderFeatures(
    {
      features: filteredFeatures,
      popoverType,
      fbUserName: record.tokenDebug?.fb_user_name || undefined,
    },
    <TagComponent />,
  );
};

export const renderAdsInsightsTag = (record: IAdAccount) => {
  const TagComponent = renderTagComponent(record.adsInsights.cell);
  let popoverType = PopoverType.FEATURES;
  if (
    record.adsInsights.accessLost &&
    record.adsInsights.features.filter((feature) => feature.errorMessage).length > 0
  ) {
    popoverType = PopoverType.AD_ACCOUNT_ACCESS_LOST;
  } else if (record.adsInsights.cell === CellStatus.DISCONNECTED) {
    popoverType = PopoverType.DISCONNECTED;
  } else if (record.adsInsights.cell === CellStatus.NA || record.adsInsights.cell === CellStatus.INACTIVE) {
    popoverType = PopoverType.HIDDEN;
  }
  return renderFeatures(
    {
      features: record.adsInsights.features,
      popoverType,
    },
    <TagComponent />,
  );
};

export const renderDraftAdsTag = (
  record: IAdAccount,
  activateAccount: () => void,
  deactivateAccount: () => void,
  defaultAccount: () => void,
) => {
  const CellComponent = renderTagComponent(record.draftAds.cell);
  let popoverType = PopoverType.FEATURES;
  if (record.draftAds.cell === CellStatus.DISCONNECTED) {
    popoverType = PopoverType.DISCONNECTED;
  }
  if (record.draftAds.cell === CellStatus.NA || record.draftAds.cell === CellStatus.INACTIVE) {
    popoverType = PopoverType.HIDDEN;
  }

  let TagComponent = <CellComponent />;
  if (record.draftAds.cell === CellStatus.INACTIVE) {
    TagComponent = (
      <span className={styles.Main}>
        <CellComponent className={styles.Hide} />
        <ActivateButton className={styles.Show} activateAccount={activateAccount} />
      </span>
    );
  }
  if (
    record.draftAds.cell === CellStatus.ACTIVE ||
    record.draftAds.cell === CellStatus.WARNING ||
    record.draftAds.cell === CellStatus.DISCONNECTED
  ) {
    TagComponent = (
      <span className={styles.Main}>
        <CellComponent className={styles.Hide} defaultAccount={record.draftAds.defaultAccount} />
        <DeactivateButton className={cx(styles.Show, styles.ButtonSpacing)} deactivateAccount={deactivateAccount} />
        {!record.draftAds.defaultAccount && (
          <SetDefaultButton className={cx(styles.Show, styles.ButtonSpacing)} setDefaultAccount={defaultAccount} />
        )}
      </span>
    );
  }
  return renderFeatures(
    {
      features: record.draftAds.features,
      popoverType,
    },
    TagComponent,
  );
};

export const renderBusinessTag = (record: IBusiness) => {
  const TagComponent = renderTagComponent(record.cell);
  const popoverType = PopoverType.FEATURES;

  return renderFeatures(
    {
      features: record.features,
      popoverType,
    },
    <TagComponent />,
  );
};

export const renderInstagramDirectMessageTag = (
  isLoadingIGDM: boolean,
  record: IInstagramAccount,
  activateAccount: () => void,
  deactivateAccount: () => void,
) => {
  const CellComponent = renderTagComponent(record.directMessaging.cell);
  const shouldDisable = !record.loading?.igdmLoading && isLoadingIGDM;
  let popoverType = PopoverType.FEATURES;
  if (record.directMessaging.cell === CellStatus.DISCONNECTED) {
    if (requiresFacebookLoginFromRecord(record)) {
      popoverType = PopoverType.FACEBOOK_LOGIN_REQUIRED;
    } else {
      popoverType = PopoverType.DISCONNECTED;
    }
  }
  if (record.directMessaging.cell === CellStatus.NA || record.directMessaging.cell === CellStatus.INACTIVE) {
    popoverType = PopoverType.HIDDEN;
  }
  if (record.directMessaging.appNotInstalled) {
    popoverType = PopoverType.IGDM_APP_NOT_INSTALLED;
  }
  if (record.directMessaging.otherClientConnected) {
    popoverType = PopoverType.IGDM_ACCOUNT_ALREADY_CONNECTED;
  }
  let TagComponent = <CellComponent />;
  if (record.directMessaging.cell === CellStatus.INACTIVE) {
    TagComponent = (
      <span className={styles.Main}>
        {!record.loading?.igdmLoading && <CellComponent className={styles.Hide} />}
        <ActivateButton
          className={cx({ [styles.Show]: !record.loading?.igdmLoading })}
          activateAccount={activateAccount}
          loading={record.loading?.igdmLoading}
          disabled={shouldDisable || disableInstagramTableButton(record.loading, 'igdm')}
        />
      </span>
    );
  }
  if (record.directMessaging.cell === CellStatus.ACTIVE || record.directMessaging.cell === CellStatus.WARNING) {
    TagComponent = (
      <span className={styles.Main}>
        {!record.loading?.igdmLoading && <CellComponent className={styles.Hide} />}
        <DeactivateButton
          className={cx({ [styles.Show]: !record.loading?.igdmLoading })}
          deactivateAccount={deactivateAccount}
          loading={record.loading?.igdmLoading}
          disabled={shouldDisable || disableInstagramTableButton(record.loading, 'igdm')}
        />
      </span>
    );
  }
  return renderFeatures(
    {
      features: record.directMessaging.features,
      popoverType,
      fbUserName: record.tokenDebug?.fb_user_name || undefined,
    },
    TagComponent,
  );
};

export const renderBCATag = (isLoadingBCA: boolean, record: IInstagramAccount, activateAccount: () => void) => {
  const shouldDisable = !record.loading?.bcaLoading && isLoadingBCA;
  const CellComponent = renderTagComponent(record.brandedContentAds.cell);
  let popoverType = PopoverType.BCA;
  if (record.brandedContentAds.cell === CellStatus.DISCONNECTED) {
    if (requiresFacebookLoginFromRecord(record)) {
      popoverType = PopoverType.FACEBOOK_LOGIN_REQUIRED;
    } else {
      popoverType = PopoverType.DISCONNECTED;
    }
  }
  if (record.brandedContentAds.cell === CellStatus.NA || record.brandedContentAds.cell === CellStatus.INACTIVE) {
    popoverType = PopoverType.HIDDEN;
  }
  if (record.brandedContentAds.otherClientConnected) {
    popoverType = PopoverType.BCA_ACCOUNT_ALREADY_CONNECTED;
  }
  let TagComponent = <CellComponent />;
  if (record.brandedContentAds.cell === CellStatus.INACTIVE) {
    TagComponent = (
      <span className={styles.Main}>
        {!record.loading?.bcaLoading && <CellComponent className={styles.Hide} />}
        <ActivateButton
          className={cx({ [styles.Show]: !record.loading?.bcaLoading })}
          activateAccount={activateAccount}
          loading={record.loading?.bcaLoading}
          disabled={shouldDisable || disableInstagramTableButton(record.loading, 'bca')}
        />
      </span>
    );
  }
  if (record.brandedContentAds.cell === CellStatus.ACTIVE) {
    TagComponent = <CellComponent />;
  }
  return renderFeatures(
    {
      features: record.brandedContentAds.features,
      popoverType,
      fbUserName: record.tokenDebug?.fb_user_name || undefined,
    },
    TagComponent,
  );
};

export const renderCreatorDiscoveryTag = (record: IInstagramAccount) => {
  const { cell, features } = record.creatorDiscovery;
  const TagComponent = renderTagComponent(cell);
  let popoverType = PopoverType.FEATURES;

  if (cell === CellStatus.DISCONNECTED) {
    if (requiresFacebookLoginFromRecord(record)) {
      popoverType = PopoverType.FACEBOOK_LOGIN_REQUIRED;
    } else {
      popoverType = PopoverType.DISCONNECTED;
    }
  } else if (cell === CellStatus.NA || cell === CellStatus.INACTIVE) {
    popoverType = PopoverType.HIDDEN;
  }

  return renderFeatures(
    {
      features,
      popoverType,
      fbUserName: record.tokenDebug?.fb_user_name || undefined,
    },
    <TagComponent />,
  );
};

export interface IAdPublisherWithPermissions {
  id: string;
  name: string;
  email: string;
  permissions: string[];
}

export const renderAdPublishersTag = (
  record: IAdAccount & { publishers?: IAdPublisherWithPermissions[] },
  openDrawer: () => void,
) => {
  const publishers = isArray(record.publishers) ? record.publishers : [];

  // Calculate counts for publish and draft permissions
  const publishCount = publishers.filter(
    (p) => isArray(p.permissions) && p.permissions.includes('meta_ads:publish'),
  ).length;

  const draftOnlyCount = publishers.filter(
    (p) =>
      isArray(p.permissions) && p.permissions.includes('meta_ads:draft') && !p.permissions.includes('meta_ads:publish'),
  ).length;

  // Create features array for the popover
  const features = [];
  if (publishCount > 0) {
    features.push({
      feature: getPublisherText(publishCount),
    });
  }
  if (draftOnlyCount > 0) {
    features.push({
      feature: getDraftText(draftOnlyCount),
    });
  }

  const breakdown = [
    publishCount > 0 ? getPublisherText(publishCount) : null,
    draftOnlyCount > 0 ? getDraftText(draftOnlyCount) : null,
  ]
    .filter(Boolean)
    .join(', ');

  const TagUI = (
    <span className={styles.Main}>
      <span className={styles.Hide}>
        <AdPublishersNumericText>{breakdown || '0'}</AdPublishersNumericText>
      </span>
      <AdPublishersEditButton
        className={styles.Show}
        onClick={() => {
          openDrawer();
        }}
      >
        Edit Members
      </AdPublishersEditButton>
    </span>
  );

  // Use renderFeatures to wrap the tag with popover
  return renderFeatures(
    {
      features,
      popoverType: PopoverType.AD_PUBLISHERS,
    },
    TagUI,
  );
};

// Helper functions for consistent pluralization
const getPublisherText = (count: number) => `${count} Publisher${count !== 1 ? 's' : ''}`;
const getDraftText = (count: number) => `${count} Draft Ad${count !== 1 ? 's' : ''}`;
